import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaVolumeUp, FaVolumeMute, FaShare, FaArrowLeft, FaStar, FaRegStar, FaImage, FaShareAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { StoryMetadata, StoryResponse } from '../../types/story';
import { ApiService } from '../../services/api';
import { StoryImageService } from '../../services/StoryImageService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../../styles/components/StoryDisplay.css';
import BackgroundSound from './BackgroundSound';
import SaveToWorkshop from '../workshop/SaveToWorkshop';

interface StoryDisplayProps {
  storyText: string;
  audioUrl?: string;
  onBack: () => void;
  storyMetadata?: StoryMetadata;
  title?: string;
  storyId?: string;
  onSaveSuccess?: (story: StoryResponse) => void;
  isFavorite?: boolean;
  onFavoriteToggle?: (isFavorite: boolean) => void;
  characters?: string[];
  hasImages?: boolean;
  imagesStatus?: string;
}

/**
 * StoryDisplay component for showing a generated story with audio playback
 */
const StoryDisplay: React.FC<StoryDisplayProps> = ({ 
  storyText, 
  audioUrl, 
  onBack,
  storyMetadata = {},
  title: providedTitle,
  storyId,
  onSaveSuccess,
  isFavorite = false,
  onFavoriteToggle,
  characters,
  hasImages = false,
  imagesStatus = 'none'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentParagraph, setCurrentParagraph] = useState(0);
  const [bgVolume, setBgVolume] = useState(0.1);
  const [bgSoundEnabled, setBgSoundEnabled] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [localIsFavorite, setLocalIsFavorite] = useState(isFavorite);
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  
  // Image-related state
  const [storyImages, setStoryImages] = useState<any[]>([]);
  const [localImagesStatus, setLocalImagesStatus] = useState(imagesStatus);
  const [localHasImages, setLocalHasImages] = useState(hasImages);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const paragraphs = storyText.split('\n\n').filter(p => p.trim());
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  
  const title = providedTitle || storyMetadata.title || 'Your Magical Story';
  
  // Estimate reading times in milliseconds per paragraph
  const readingTimes = paragraphs.map(p => p.split(' ').length * 500);
  
  // Add new state for image modal and carousel
  const [modalOpen, setModalOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  
  // Ref for the carousel interval
  const carouselIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Initialize audio
  useEffect(() => {
    if (audioUrl) {
      console.log('Initializing audio with URL:', audioUrl);
      console.log('Audio URL type:', typeof audioUrl);
      console.log('Audio URL starts with http:', audioUrl.startsWith('http'));
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      // Set up audio event listeners
      audio.addEventListener('ended', handleStoryEnd);
      audio.addEventListener('pause', () => setIsPlaying(false));
      audio.addEventListener('play', () => setIsPlaying(true));
      audio.addEventListener('canplaythrough', () => {
        console.log('Audio can play through - loaded successfully');
        setIsAudioLoaded(true);
      });
      
      audio.addEventListener('error', (e) => {
        console.error('Audio loading error:', e);
        console.error('Audio error code:', audio.error?.code);
        console.error('Audio error message:', audio.error?.message);
        setIsAudioLoaded(false);
      });
      
      // Preload audio
      audio.load();
      
      // Clean up on unmount
      return () => {
        audio.pause();
        audio.removeEventListener('ended', handleStoryEnd);
        audio.removeEventListener('pause', () => setIsPlaying(false));
        audio.removeEventListener('play', () => setIsPlaying(true));
        audio.removeEventListener('canplaythrough', () => setIsAudioLoaded(true));
      };
    }
  }, [audioUrl]);
  
  // Auto-scroll through paragraphs at appropriate times if playing
  useEffect(() => {
    if (!isPlaying || !audioRef.current) return;
    
    // Don't advance past the last paragraph
    if (currentParagraph >= paragraphs.length - 1) return;
    
    // Schedule advancing to the next paragraph
    const timerId = setTimeout(() => {
      setCurrentParagraph(prev => prev + 1);
    }, readingTimes[currentParagraph]);
    
    return () => clearTimeout(timerId);
  }, [isPlaying, currentParagraph, paragraphs.length, readingTimes]);
  
  const togglePlayback = () => {
    if (!audioRef.current) {
      console.error('Cannot toggle playback - audio reference is null');
      return;
    }
    
    console.log('Toggle playback. Current state:', isPlaying ? 'playing' : 'paused');
    
    if (isPlaying) {
      console.log('Pausing audio');
      audioRef.current.pause();
    } else {
      // If story ended, start from beginning
      if (audioRef.current.ended) {
        console.log('Audio ended, restarting from beginning');
        audioRef.current.currentTime = 0;
        setCurrentParagraph(0);
      }
      console.log('Playing audio');
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
      });
    }
  };
  
  const handleStoryEnd = () => {
    setIsPlaying(false);
    setCurrentParagraph(0);
  };
  
  const handleParagraphClick = (index: number) => {
    setCurrentParagraph(index);
    
    // Estimate where in the audio to seek to
    if (audioRef.current) {
      const totalDuration = audioRef.current.duration;
      const totalWords = paragraphs.reduce((sum, p) => sum + p.split(' ').length, 0);
      const wordsSoFar = paragraphs
        .slice(0, index)
        .reduce((sum, p) => sum + p.split(' ').length, 0);
      
      // Seek proportionally
      audioRef.current.currentTime = (wordsSoFar / totalWords) * totalDuration;
    }
  };
  
  const handleBgVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setBgVolume(newVolume);
    console.log(`Background volume changed to: ${newVolume}`);
  };
  
  const toggleBgSound = () => {
    const newBgSoundEnabled = !bgSoundEnabled;
    setBgSoundEnabled(newBgSoundEnabled);
    console.log(`Background sound ${newBgSoundEnabled ? 'enabled' : 'disabled'}`);
    
    // When toggling on, reset to a reasonable volume if it was 0
    if (newBgSoundEnabled && bgVolume === 0) {
      setBgVolume(0.1);
    }
    
    // If enabling, add a small visual feedback
    if (newBgSoundEnabled) {
      const bgButton = document.querySelector('.bg-sound-toggle');
      if (bgButton) {
        bgButton.classList.add('pulse');
        setTimeout(() => {
          bgButton.classList.remove('pulse');
        }, 500);
      }
    }
  };
  
  const handleToggleFavorite = async () => {
    try {
      setIsFavoriteLoading(true);
      console.log("Toggling favorite state from:", localIsFavorite, "to:", !localIsFavorite);
      
      const token = await getAccessToken();
      if (!token || !storyId) {
        console.error("Cannot toggle favorite: missing token or storyId");
        return;
      }
      
      // Toggle favorite status
      await ApiService.updateStoryFavorite(storyId, !localIsFavorite);
      
      // Update local state
      setLocalIsFavorite(!localIsFavorite);
      
      // For debugging
      console.log("Favorite state updated to:", !localIsFavorite);
      
      // Call the callback if provided
      if (onFavoriteToggle) {
        onFavoriteToggle(!localIsFavorite);
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
    } finally {
      setIsFavoriteLoading(false);
    }
  };
  
  const handleShare = async () => {
    try {
      setIsSharing(true);
      
      const token = await getAccessToken();
      if (!token) {
        console.error('User not authenticated');
        return;
      }
      
      // If the story doesn't have an ID, we need to alert the user
      if (!storyId) {
        console.error('Story ID is missing');
        return;
      }
      
      // Make the story public
      const updatedStory = await ApiService.updateStoryVisibility(storyId, true);
      
      // Copy the share link to clipboard
      const shareUrl = `${window.location.origin}/shared/${updatedStory.shareCode}`;
      await navigator.clipboard.writeText(shareUrl);
      
      // Show success message - replace with a nicer notification
      alert('Share link copied to clipboard! You can now share this URL with others.');
      
      // Call the onSaveSuccess callback if provided
      if (onSaveSuccess) {
        onSaveSuccess(updatedStory);
      }
      
    } catch (err) {
      console.error('Error sharing story:', err);
    } finally {
      setIsSharing(false);
    }
  };
  
  // Set up initial favorite state from props
  useEffect(() => {
    setLocalIsFavorite(isFavorite);
    console.log("StoryDisplay received storyId:", storyId, "and isFavorite:", isFavorite);
  }, [isFavorite, storyId]);
  
  // Check for image status and load images if needed
  useEffect(() => {
    const checkImageStatus = async () => {
      if (!storyId) return;
      
      try {
        setIsLoadingImages(true);
        
        // Get current image status
        const imageStatus = await StoryImageService.getImageGenerationStatus(storyId);
        setLocalImagesStatus(imageStatus.images_status);
        setLocalHasImages(imageStatus.has_images);
        
        // If images are available, load them
        if (imageStatus.has_images) {
          const images = await StoryImageService.getStoryImages(storyId);
          console.log('Loaded story images:', images);
          setStoryImages(images);
        }
        
        // If status is 'pending', set up polling
        if (imageStatus.images_status === 'pending') {
          // Set up subscription to image updates
          const unsubscribe = StoryImageService.subscribeToImageUpdates(
            storyId, 
            (newStatus) => {
              setLocalImagesStatus(newStatus.images_status);
              setLocalHasImages(newStatus.has_images);
              
              // If status changes to 'completed', load the images
              if (newStatus.images_status === 'completed' && newStatus.has_images) {
                StoryImageService.getStoryImages(storyId).then(images => {
                  setStoryImages(images);
                });
              }
            }
          );
          
          // Clean up subscription when component unmounts
          return () => {
            unsubscribe();
          };
        }
      } catch (error) {
        console.error('Error checking image status:', error);
      } finally {
        setIsLoadingImages(false);
      }
    };
    
    checkImageStatus();
  }, [storyId]);

  // Function to open modal with specific image
  const openImageModal = (index: number) => {
    setActiveImageIndex(index);
    setModalOpen(true);
    
    // Auto-start carousel when modal opens
    // Start carousel - change image every 5 seconds
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
    }
    carouselIntervalRef.current = setInterval(() => {
      navigateImage('next');
    }, 5000);
    
    // Pause audio when opening modal if it's playing
    if (isPlaying) {
      togglePlayback();
    }
  };
  
  // Function to close modal
  const closeModal = () => {
    setModalOpen(false);
    // Clear any carousel intervals
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
      carouselIntervalRef.current = null;
    }
  };
  
  // Navigate to previous or next image
  const navigateImage = (direction: 'prev' | 'next') => {
    if (storyImages.length <= 1) return;
    
    setActiveImageIndex(prevIndex => {
      if (direction === 'prev') {
        return prevIndex === 0 ? storyImages.length - 1 : prevIndex - 1;
      } else {
        return prevIndex === storyImages.length - 1 ? 0 : prevIndex + 1;
      }
    });
  };
  
  // Clean up carousel interval on unmount
  useEffect(() => {
    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
    };
  }, []);
  
  // Close modal on escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalOpen) {
        closeModal();
      } else if (e.key === 'ArrowLeft' && modalOpen) {
        navigateImage('prev');
      } else if (e.key === 'ArrowRight' && modalOpen) {
        navigateImage('next');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalOpen]);

  // Modify the renderImageGallery function to remove the reading carousel toggle
  const renderImageGallery = () => {
    if (!storyId) return null;
    
    if (localImagesStatus === 'pending') {
      return (
        <div className="story-images-section pending">
          <h3>AI Images</h3>
          <div className="image-status-message">
            <div className="loading-spinner"></div>
            <p>Generating magical images for your story...</p>
            <p className="small">This may take a few minutes. The images will appear here once ready.</p>
          </div>
        </div>
      );
    }
    
    if (localImagesStatus === 'failed') {
      return (
        <div className="story-images-section error">
          <h3>AI Images</h3>
          <div className="image-status-message error">
            <p>We couldn't generate images for this story. Please try again later.</p>
          </div>
        </div>
      );
    }
    
    if (localHasImages && storyImages.length > 0) {
      return (
        <div className="story-images-section">
          <h3>Story Illustrations</h3>
          
          <div className="image-gallery">
            {storyImages.map((image, index) => (
              <div 
                key={index} 
                className="story-image-container"
                onClick={() => openImageModal(index)}
              >
                <img 
                  src={image.image_path} 
                  alt={`Story illustration ${index + 1}`} 
                  className="story-image"
                />
                <p className="image-caption">{image.caption || `Scene ${index + 1}`}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Add the image modal component
  const renderImageModal = () => {
    if (!modalOpen || storyImages.length === 0) return null;
    
    const currentImage = storyImages[activeImageIndex];
    
    return (
      <div className="image-modal-overlay" onClick={closeModal}>
        <div className="image-modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close-button" onClick={closeModal}>
            <FaTimes />
          </button>
          
          <div className="modal-image-container">
            {storyImages.length > 1 && (
              <button 
                className="modal-nav-button prev"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('prev');
                }}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>
            )}
            
            <img 
              src={currentImage.image_path} 
              alt={currentImage.caption || `Story illustration ${activeImageIndex + 1}`} 
              className="modal-image"
            />
            
            {storyImages.length > 1 && (
              <button 
                className="modal-nav-button next"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('next');
                }}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
          
          <div className="modal-caption">
            <p>{currentImage.caption || `Scene ${activeImageIndex + 1}`}</p>
            <div className="modal-controls">
              <div className="modal-pagination">
                {activeImageIndex + 1} / {storyImages.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="story-display-container">
      <div className="story-header" style={{ position: 'relative' }}>
        <button className="back-button" onClick={onBack}>
          {/* @ts-ignore */}
          <FaArrowLeft /> Back
        </button>
        <h1>{title}</h1>
        {storyId && (
          <button 
            className={`favorite-button ${localIsFavorite ? 'favorited' : ''}`} 
            onClick={handleToggleFavorite}
            disabled={isFavoriteLoading}
            aria-label={localIsFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            {/* @ts-ignore */}
            {localIsFavorite ? <FaStar /> : <FaRegStar />}
          </button>
        )}
      </div>
      
      <div className="story-content">
        {paragraphs.map((paragraph, index) => (
          <p 
            key={index}
            className={`story-paragraph ${index === currentParagraph ? 'active' : ''}`}
            onClick={() => handleParagraphClick(index)}
          >
            {paragraph}
          </p>
        ))}
      </div>
      
      {/* Render Image Gallery */}
      {renderImageGallery()}
      
      {/* Render Image Modal */}
      {renderImageModal()}
      
      <div className="story-audio-panel">
        <div className="play-controls">
          <button 
            className={`audio-button play-button ${isPlaying ? 'active' : ''}`}
            onClick={togglePlayback}
            disabled={!audioUrl || !isAudioLoaded}
          >
            <div className="bg-toggle-icon">
              {/* @ts-ignore */}
              {isPlaying ? <FaVolumeMute /> : <FaVolumeUp />}
            </div>
            <span>{isPlaying ? 'Pause Narration' : 'Play Narration'}</span>
          </button>
          
          <div className="background-noise-wrapper">
            <div className="background-noise-controls">
              <div className="bg-controls-header">
                <button 
                  className={`bg-sound-toggle ${bgSoundEnabled ? 'active' : ''}`}
                  onClick={toggleBgSound}
                  aria-label={bgSoundEnabled ? "Turn off brown noise" : "Turn on brown noise"}
                >
                  <span className="bg-toggle-icon">{bgSoundEnabled ? '🔊' : '🔇'}</span>
                  <span className="bg-toggle-text">Brown Noise: <strong>{bgSoundEnabled ? 'ON' : 'OFF'}</strong></span>
                  {bgSoundEnabled && <span className="bg-sound-indicator">•</span>}
                </button>
                <div className="volume-indicator">
                  {Math.round(bgVolume * 100)}%
                </div>
              </div>
              
              <input 
                type="range" 
                id="bg-volume"
                min="0" 
                max="0.3" 
                step="0.01" 
                value={bgVolume}
                onChange={handleBgVolumeChange}
                disabled={!bgSoundEnabled}
                className={!bgSoundEnabled ? 'disabled' : ''}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="story-actions">
        <button className="action-button share-button" onClick={handleShare} disabled={isSharing || !storyId}>
          {/* @ts-ignore */}
          <FaShare />
          <span>Share</span>
        </button>
      </div>
      
      <BackgroundSound 
        isPlaying={bgSoundEnabled && (audioUrl ? isPlaying : true)} 
        volume={bgVolume}
      />
      
      <hr className="section-divider" />
      
      <SaveToWorkshop 
        setting={storyMetadata?.theme || storyMetadata?.backgroundNoise}
        characters={characters}
      />
    </div>
  );
};

export default StoryDisplay; 